import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
    Avatar,
    Alert,
    Box,
    Button,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import { alpha } from '@mui/material/styles';
import { API_BASE_URL, tryPaths } from './api';
import { AnalyticsInlineState, AnalyticsMetricCard, AnalyticsPageHero, AnalyticsPanelSection, glassSubCardSx } from './analyticsSurface';

const CLIENTS_KEY = 'monsurv_clients';
const CLIENT_TYPE_BROKER = 'broker';
const CLIENT_TYPE_FUNDER_INVESTOR = 'funder_investor';

function normalizeClientType(value) {
    const raw = String(value || '').trim().toLowerCase();
    if (raw === CLIENT_TYPE_BROKER) return CLIENT_TYPE_BROKER;
    return CLIENT_TYPE_FUNDER_INVESTOR;
}

function normalizeRequiresMonsurvAccess(value, clientType) {
    const type = normalizeClientType(clientType);
    if (type === CLIENT_TYPE_BROKER) return true;
    return Boolean(value);
}

function buildBlankClientForm({ montech = false } = {}) {
    return {
        companyName: '',
        headOfficeAddress: '',
        phone: '',
        email: '',
        contacts: [{ name: '', phone: '', email: '' }],
        clientType: montech ? CLIENT_TYPE_FUNDER_INVESTOR : CLIENT_TYPE_FUNDER_INVESTOR,
        requiresMonsurvAccess: false,
    };
}

function rgbToHex(r, g, b) {
    const clamp = (n) => Math.max(0, Math.min(255, Math.round(Number(n) || 0)));
    const to2 = (n) => clamp(n).toString(16).padStart(2, '0');
    return `#${to2(r)}${to2(g)}${to2(b)}`;
}

function sampleBorderAverageColor(img) {
    const size = 32;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(img, 0, 0, size, size);
    const data = ctx.getImageData(0, 0, size, size).data;

    const idx = (x, y) => (y * size + x) * 4;
    const coords = [];
    for (let x = 0; x < size; x += 1) {
        coords.push([x, 0], [x, size - 1]);
    }
    for (let y = 1; y < size - 1; y += 1) {
        coords.push([0, y], [size - 1, y]);
    }

    let r = 0;
    let g = 0;
    let b = 0;
    let n = 0;
    for (const [x, y] of coords) {
        const i = idx(x, y);
        const a = data[i + 3];
        if (a < 12) continue;
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        n += 1;
    }
    if (!n) return null;
    return rgbToHex(r / n, g / n, b / n);
}

function loadClients() {
    try { const raw = localStorage.getItem(CLIENTS_KEY); return raw ? JSON.parse(raw) : []; } catch (e) { return []; }
}

function saveClients(list) {
    try { localStorage.setItem(CLIENTS_KEY, JSON.stringify(list)); } catch (e) { }
}

// "Funder" page backend endpoints often vary; support a few common paths.
const FUNDERS_API_PATHS = ['/funders', '/api/funders', '/clients', '/api/clients'];

function getAuthHeader() {
    try {
        const token = localStorage.getItem('monsurv_auth_token') || '';
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

function normalizeFunder(f) {
    if (!f || typeof f !== 'object') return null;
    const rawContacts = Array.isArray(f.contacts) ? f.contacts : (Array.isArray(f.contact_list) ? f.contact_list : []);
    const contacts = rawContacts.map((c) => ({
        name: c?.name ?? c?.contact_name ?? '',
        phone: c?.phone ?? c?.contact_phone ?? '',
        email: c?.email ?? c?.contact_email ?? ''
    })).filter((c) => c.name || c.phone || c.email);
    return {
        id: f.id ?? f.funder_id ?? f.client_id,
        companyName: f.companyName ?? f.company_name ?? f.name ?? '',
        headOfficeAddress: f.headOfficeAddress ?? f.head_office_address ?? '',
        leadContact: f.leadContact ?? f.lead_contact ?? f.contact ?? '',
        phone: f.phone ?? '',
        email: f.email ?? '',
        logo_url: f.logo_url ?? f.logoUrl ?? null,
        clientType: normalizeClientType(f.clientType ?? f.client_type),
        requiresMonsurvAccess: normalizeRequiresMonsurvAccess(
            f.requiresMonsurvAccess ?? f.requires_monsurv_access,
            f.clientType ?? f.client_type
        ),
        contacts
    };
}

function getInitials(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

async function fetchFunderMetricsFromApi() {
    const data = await tryPaths(
        ['/funders/metrics', '/api/funders/metrics'],
        (path) => `${API_BASE_URL}${path}`,
        { method: 'GET' }
    );
    const list = Array.isArray(data?.metrics) ? data.metrics : (Array.isArray(data) ? data : []);
    return list.map((m) => ({
        funder_id: m.funder_id ?? m.funderId ?? null,
        funder_key: String(m.funder_key || '').trim().toLowerCase(),
        active_projects: Number(m.active_projects || 0),
        active_facility: Number(m.active_facility || 0),
        initial_reports_in_progress: Number(m.initial_reports_in_progress || 0),
        interim_reports: Number(m.interim_reports || 0),
        high_risk_projects: Number(m.high_risk_projects || 0),
        medium_risk_projects: Number(m.medium_risk_projects || 0),
        low_risk_projects: Number(m.low_risk_projects || 0)
    }));
}

function formatCurrencyGBP(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n);
}

async function fetchFundersFromApi() {
    const data = await tryPaths(
        FUNDERS_API_PATHS,
        (path) => `${API_BASE_URL}${path}`,
        { method: 'GET' }
    );
    const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
    return list.map(normalizeFunder).filter(Boolean);
}

async function createFunderOnApi(payload) {
    return tryPaths(
        FUNDERS_API_PATHS,
        (path) => `${API_BASE_URL}${path}`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify(payload)
        }
    );
}

async function updateFunderOnApi(id, payload) {
    return tryPaths(
        FUNDERS_API_PATHS,
        (path) => `${API_BASE_URL}${path}/${encodeURIComponent(id)}`,
        {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify(payload)
        }
    );
}

async function deleteFunderOnApi(id, deletionPassword) {
    return tryPaths(
        FUNDERS_API_PATHS,
        (path) => `${API_BASE_URL}${path}/${encodeURIComponent(id)}`,
        {
            method: 'DELETE',
            headers: {
                ...getAuthHeader(),
                ...(deletionPassword ? { 'X-Deletion-Password': String(deletionPassword) } : {})
            }
        }
    );
}

export default function Clients({ authUser }) {
    const [clients, setClients] = useState(() => loadClients());
    const tenantSlug = String(authUser?.tenant_slug || '').trim().toLowerCase();
    const isMontechTenant = tenantSlug === 'montech';
    const [form, setForm] = useState(() => buildBlankClientForm({ montech: isMontechTenant }));
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState(() => buildBlankClientForm({ montech: isMontechTenant }));
    const [showClientForm, setShowClientForm] = useState(false);
    const [expandedIds, setExpandedIds] = useState([]);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuClientId, setMenuClientId] = useState(null);
    const [metrics, setMetrics] = useState([]);
    const [logoFile, setLogoFile] = useState(null);
    const [logoError, setLogoError] = useState('');
    const [requestItems, setRequestItems] = useState([]);
    const [requestsLoading, setRequestsLoading] = useState(false);
    const [requestsError, setRequestsError] = useState('');
    const [requestNotice, setRequestNotice] = useState('');
    const [requestNoticeSeverity, setRequestNoticeSeverity] = useState('info');
    const requestToastTimerRef = useRef(null);

    const [logoStyleByClientId, setLogoStyleByClientId] = useState(() => ({}));

    const logoLoadKey = useMemo(() => {
        try {
            return (Array.isArray(clients) ? clients : [])
                .map((c) => `${String(c?.id || '')}:${String(c?.logo_url || '')}`)
                .join('|');
        } catch {
            return '';
        }
    }, [clients]);

    const [adminRequestEdits, setAdminRequestEdits] = useState({});
    const [adminReviewOpen, setAdminReviewOpen] = useState(false);
    const [adminReviewItem, setAdminReviewItem] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [deleteTargetLabel, setDeleteTargetLabel] = useState('');
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectTargetId, setRejectTargetId] = useState(null);
    const [rejectNote, setRejectNote] = useState('');
    const [rejectBusy, setRejectBusy] = useState(false);
    const [rejectError, setRejectError] = useState('');

    const isHydratedFromApi = useRef(false);
    const authRole = String(authUser?.role || '').trim().toLowerCase();
    const isAdmin = authRole === 'admin';
    const isSurveyor = authRole === 'surveyor' || authRole === 'standard';

    useEffect(() => {
        setForm((prev) => ({
            ...buildBlankClientForm({ montech: isMontechTenant }),
            ...prev,
            clientType: isMontechTenant ? normalizeClientType(prev?.clientType) : CLIENT_TYPE_FUNDER_INVESTOR,
            requiresMonsurvAccess: isMontechTenant
                ? normalizeRequiresMonsurvAccess(prev?.requiresMonsurvAccess, prev?.clientType)
                : false
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMontechTenant]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const list = await fetchFundersFromApi();
                if (!cancelled) {
                    isHydratedFromApi.current = true;
                    setClients(list);
                }
            } catch (e) {
                // Backend not available yet; keep localStorage fallback.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function loadOne(clientId, src) {
            return new Promise((resolve) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    const w = Number(img.naturalWidth || 0);
                    const h = Number(img.naturalHeight || 0);
                    const ratio = (w > 0 && h > 0) ? (w / h) : null;
                    let bg = null;
                    try {
                        bg = sampleBorderAverageColor(img);
                    } catch {
                        bg = null;
                    }
                    resolve({ id: String(clientId), ratio, bgColor: bg || '#ffffff' });
                };
                img.onerror = () => resolve({ id: String(clientId), ratio: null, bgColor: '#ffffff' });
                img.src = src;
            });
        }

        (async () => {
            const wanted = (Array.isArray(clients) ? clients : [])
                .map((c) => {
                    const id = c?.id;
                    const raw = String(c?.logo_url || '').trim();
                    if (!id || !raw) return null;
                    const src = /^https?:\/\//i.test(raw) ? raw : `${API_BASE_URL}${raw}`;
                    return { id: String(id), src };
                })
                .filter(Boolean);

            if (!wanted.length) {
                if (!cancelled) setLogoStyleByClientId({});
                return;
            }

            const results = await Promise.all(wanted.map((w) => loadOne(w.id, w.src)));
            if (cancelled) return;
            setLogoStyleByClientId((prev) => {
                const next = { ...(prev || {}) };
                for (const r of results) {
                    const ratio = typeof r.ratio === 'number' && Number.isFinite(r.ratio) ? r.ratio : null;
                    next[String(r.id)] = {
                        bgColor: String(r.bgColor || '#ffffff'),
                        pad: ratio !== null && ratio >= 1.6 ? 8 : 6
                    };
                }
                return next;
            });
        })();

        return () => { cancelled = true; };
    }, [clients, logoLoadKey]);

    useEffect(() => {
        reloadRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser, isAdmin]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const list = await fetchFunderMetricsFromApi();
                if (!cancelled) setMetrics(list);
            } catch {
                if (!cancelled) setMetrics([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => { saveClients(clients); try { window.dispatchEvent(new CustomEvent('monsurv:clients-updated', { detail: clients })); } catch (e) { } }, [clients]);

    function setLogoFileFromInput(file) {
        if (!isAdmin) {
            setLogoFile(null);
            setLogoError('Logo upload requires admin approval.');
            return;
        }
        if (!file) {
            setLogoFile(null);
            setLogoError('');
            return;
        }
        const mimetype = String(file.type || '').toLowerCase();
        const ok = ['image/jpeg', 'image/png', 'image/webp'].includes(mimetype);
        if (!ok) {
            setLogoFile(null);
            setLogoError('Only JPG, PNG, or WebP images are allowed.');
            return;
        }
        setLogoError('');
        setLogoFile(file);
    }

    function handleChange(e) { const { name, value } = e.target; setForm(f => ({ ...f, [name]: value })); }
    function updateContactRow(list, index, key, value) {
        return list.map((c, i) => (i === index ? { ...c, [key]: value } : c));
    }
    function normalizeContacts(list) {
        const arr = Array.isArray(list) ? list : [];
        return arr
            .map((c) => ({
                name: String(c?.name || '').trim(),
                phone: String(c?.phone || '').trim(),
                email: String(c?.email || '').trim()
            }))
            .filter((c) => c.name || c.phone || c.email);
    }

    function showRequestToast(message, severity) {
        const msg = String(message || '').trim();
        if (!msg) return;
        setRequestNotice(msg);
        setRequestNoticeSeverity(severity || 'info');
        if (requestToastTimerRef.current) {
            window.clearTimeout(requestToastTimerRef.current);
            requestToastTimerRef.current = null;
        }
        const ms = (severity === 'success') ? 5000 : 7000;
        requestToastTimerRef.current = window.setTimeout(() => {
            setRequestNotice('');
            requestToastTimerRef.current = null;
        }, ms);
    }

    useEffect(() => {
        return () => {
            if (requestToastTimerRef.current) window.clearTimeout(requestToastTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function normalizeRequestPayload(payloadLike) {
        const p = payloadLike && typeof payloadLike === 'object' ? payloadLike : {};
        const contacts = normalizeContacts(Array.isArray(p.contacts) ? p.contacts : []);
        const clientType = isMontechTenant ? normalizeClientType(p.clientType ?? p.client_type) : CLIENT_TYPE_FUNDER_INVESTOR;
        const requiresMonsurvAccess = isMontechTenant
            ? normalizeRequiresMonsurvAccess(p.requiresMonsurvAccess ?? p.requires_monsurv_access, clientType)
            : false;
        return {
            companyName: p.companyName ?? p.company_name ?? p.name ?? '',
            headOfficeAddress: p.headOfficeAddress ?? p.head_office_address ?? '',
            phone: p.phone ?? '',
            email: p.email ?? '',
            contacts: contacts.length ? contacts : [{ name: '', phone: '', email: '' }],
            clientType,
            requiresMonsurvAccess
        };
    }

    function openAdminReview(item) {
        if (!isAdmin) return;
        const id = String(item?.id ?? '');
        if (!id) return;
        setAdminReviewItem(item || null);
        setAdminReviewOpen(true);
        setAdminRequestEdits((prev) => {
            if (prev && typeof prev === 'object' && prev[id]) return prev;
            const normalized = normalizeRequestPayload(item?.payload);
            return { ...(prev || {}), [id]: normalized };
        });
    }

    function closeAdminReview() {
        setAdminReviewOpen(false);
        setAdminReviewItem(null);
    }

    async function fetchFunderRequests({ mine = false, status = '' } = {}) {
        const qs = new URLSearchParams();
        if (mine) qs.set('mine', '1');
        if (status) qs.set('status', status);

        return tryPaths(
            ['/funders/requests', '/api/funders/requests'],
            (path) => `${API_BASE_URL}${path}${qs.toString() ? `?${qs.toString()}` : ''}`,
            { method: 'GET', headers: { ...getAuthHeader() } }
        );
    }

    async function submitFunderRequest({ requestType, funderId, payload }) {
        return tryPaths(
            ['/funders/requests', '/api/funders/requests'],
            (path) => `${API_BASE_URL}${path}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({ request_type: requestType, funder_id: funderId, payload })
            }
        );
    }

    async function approveRequest(id, payloadOverride) {
        const body = payloadOverride && typeof payloadOverride === 'object' ? { payload: payloadOverride } : null;
        const resp = await fetch(`${API_BASE_URL}/funders/requests/${encodeURIComponent(id)}/approve`, {
            method: 'POST',
            headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...getAuthHeader() },
            ...(body ? { body: JSON.stringify(body) } : {})
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(json?.error || 'Failed to approve request');
        return json;
    }

    async function rejectRequest(id, note) {
        const resp = await fetch(`${API_BASE_URL}/funders/requests/${encodeURIComponent(id)}/reject`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify({ note })
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(json?.error || 'Failed to reject request');
        return json;
    }

    async function reloadRequests() {
        if (!authUser) return;
        setRequestsLoading(true);
        setRequestsError('');
        try {
            const data = await fetchFunderRequests({
                mine: !isAdmin,
                status: isAdmin ? 'pending' : ''
            });
            const list = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
            setRequestItems(list);
        } catch (e) {
            setRequestsError(e?.message || 'Failed to load requests');
        } finally {
            setRequestsLoading(false);
        }
    }

    async function reloadClients() {
        if (!isHydratedFromApi.current) return;
        try {
            const list = await fetchFundersFromApi();
            setClients(list);
        } catch {
            // ignore
        }
    }
    async function addClient(e) {
        e.preventDefault();
        if (!form.companyName) return;
        setRequestNotice('');

        const clientType = isMontechTenant ? normalizeClientType(form.clientType) : CLIENT_TYPE_FUNDER_INVESTOR;
        const requiresMonsurvAccess = isMontechTenant
            ? normalizeRequiresMonsurvAccess(form.requiresMonsurvAccess, clientType)
            : false;
        const normalizedContacts = normalizeContacts(form.contacts);
        if (requiresMonsurvAccess && normalizedContacts.length < 1) {
            showRequestToast('At least one contact is required when Monsurv access is enabled.', 'error');
            return;
        }
        const invalidContact = requiresMonsurvAccess
            ? normalizedContacts.find((contact) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(contact.email || '').trim()))
            : null;
        if (invalidContact) {
            showRequestToast(`${invalidContact.name || 'Each contact'} must have a valid email address.`, 'error');
            return;
        }

        const payload = {
            companyName: form.companyName,
            headOfficeAddress: form.headOfficeAddress,
            phone: form.phone,
            email: form.email,
            contacts: normalizedContacts,
            clientType,
            requiresMonsurvAccess
        };

        if (!isAdmin) {
            try {
                await submitFunderRequest({ requestType: 'create', funderId: null, payload });
                showRequestToast('Submitted for admin approval.', 'success');
                await reloadRequests();
            } catch (e) {
                showRequestToast(e?.message || 'Failed to submit for approval.', 'error');
            }

            setForm(buildBlankClientForm({ montech: isMontechTenant }));
            setLogoFile(null);
            setLogoError('');
            setShowClientForm(false);
            return;
        }

        if (isHydratedFromApi.current) {
            try {
                const created = await createFunderOnApi(payload);
                const normalized = normalizeFunder(created?.data || created) || { id: 'c-' + Date.now().toString(36), ...payload };
                const provisioning = created?.provisioning || created?.data?.provisioning || {};
                let logoUrl = null;
                if (logoFile && normalized?.id) {
                    try {
                        const fd = new FormData();
                        fd.append('logo', logoFile);
                        const resp = await fetch(`${API_BASE_URL}/funders/${encodeURIComponent(normalized.id)}/logo`, {
                            method: 'POST',
                            headers: { ...getAuthHeader() },
                            body: fd
                        });
                        const json = await resp.json().catch(() => null);
                        if (resp.ok) logoUrl = json?.logo_url || null;
                    } catch {
                        // ignore logo upload failures
                    }
                }
                setClients(list => [{ ...normalized, ...(logoUrl ? { logo_url: logoUrl } : {}) }, ...list]);
                const failedInvites = Array.isArray(provisioning.failed_emails) ? provisioning.failed_emails : [];
                if (failedInvites.length) {
                    showRequestToast(`Client added, but invitations could not be sent to: ${failedInvites.map((item) => item.email).join(', ')}.`, 'warning');
                } else if (Number(provisioning.sent_emails) > 0) {
                    showRequestToast(`Client added and ${provisioning.sent_emails} contact invitation${Number(provisioning.sent_emails) === 1 ? '' : 's'} sent.`, 'success');
                } else {
                    showRequestToast('Client added.', 'success');
                }
            } catch (e) {
                showRequestToast(e?.message || 'Failed to add client.', 'error');
                return;
            }
        } else {
            const item = { id: 'c-' + Date.now().toString(36), ...payload };
            setClients(list => [item, ...list]);
        }

        setForm(buildBlankClientForm({ montech: isMontechTenant }));
        setLogoFile(null);
        setLogoError('');
    }

    async function removeClient(id) {
        if (!isAdmin) return;

        const client = clients.find((c) => String(c?.id) === String(id)) || null;
        const label = client?.companyName ? `"${client.companyName}"` : 'this client';

        setDeleteTargetId(id);
        setDeleteTargetLabel(label);
        setDeletePassword('');
        setDeleteError('');
        setDeleteDialogOpen(true);
    }

    function closeDeleteDialog() {
        if (deleteBusy) return;
        setDeleteDialogOpen(false);
        setDeleteTargetId(null);
        setDeleteTargetLabel('');
        setDeletePassword('');
        setDeleteError('');
    }

    async function confirmDeleteClient() {
        if (!isAdmin) return;
        const id = deleteTargetId;
        const pw = String(deletePassword || '').trim();
        if (id === null || id === undefined || id === '') return;
        if (!pw) {
            setDeleteError('Deletion password is required.');
            return;
        }

        setDeleteBusy(true);
        setDeleteError('');
        try {
            if (isHydratedFromApi.current) {
                await deleteFunderOnApi(id, pw);
            }

            setClients(list => list.filter(c => c.id !== id));
            if (editingId === id) cancelEdit();

            setDeleteDialogOpen(false);
            setDeleteTargetId(null);
            setDeleteTargetLabel('');
            setDeletePassword('');
            setDeleteError('');
        } catch (e) {
            setDeleteError(String(e?.message || 'Failed to delete client'));
        } finally {
            setDeleteBusy(false);
        }
    }

    function openRejectDialog(requestId) {
        const id = requestId;
        if (id === null || id === undefined || id === '') return;
        setRejectTargetId(id);
        setRejectNote('');
        setRejectError('');
        setRejectDialogOpen(true);
    }

    function closeRejectDialog() {
        if (rejectBusy) return;
        setRejectDialogOpen(false);
        setRejectTargetId(null);
        setRejectNote('');
        setRejectError('');
    }

    async function confirmRejectRequest() {
        if (!rejectTargetId) return;
        setRejectBusy(true);
        setRejectError('');
        try {
            await rejectRequest(rejectTargetId, String(rejectNote || ''));
            showRequestToast('Rejected.', 'success');
            closeRejectDialog();
            closeAdminReview();
            await reloadRequests();
        } catch (e) {
            setRejectError(String(e?.message || 'Failed to reject request.'));
        } finally {
            setRejectBusy(false);
        }
    }
    function startEdit(c) {
        setEditingId(c.id);
        const clientType = isMontechTenant ? normalizeClientType(c.clientType ?? c.client_type) : CLIENT_TYPE_FUNDER_INVESTOR;
        setEditForm({
            companyName: c.companyName || c.name || '',
            headOfficeAddress: c.headOfficeAddress || '',
            phone: c.phone || '',
            email: c.email || '',
            contacts: Array.isArray(c.contacts) && c.contacts.length ? c.contacts : [{ name: '', phone: '', email: '' }],
            clientType,
            requiresMonsurvAccess: isMontechTenant
                ? normalizeRequiresMonsurvAccess(c.requiresMonsurvAccess ?? c.requires_monsurv_access, clientType)
                : false
        });
    }
    function cancelEdit() {
        setEditingId(null);
        setEditForm(buildBlankClientForm({ montech: isMontechTenant }));
    }
    async function saveEdit(e) {
        e.preventDefault();
        if (!editForm.companyName) return;

        const clientType = isMontechTenant ? normalizeClientType(editForm.clientType) : CLIENT_TYPE_FUNDER_INVESTOR;
        const requiresMonsurvAccess = isMontechTenant
            ? normalizeRequiresMonsurvAccess(editForm.requiresMonsurvAccess, clientType)
            : false;
        const normalizedContacts = normalizeContacts(editForm.contacts);
        if (requiresMonsurvAccess && normalizedContacts.length < 1) {
            showRequestToast('At least one contact is required when Monsurv access is enabled.', 'error');
            return;
        }

        const payload = {
            companyName: editForm.companyName,
            headOfficeAddress: editForm.headOfficeAddress,
            phone: editForm.phone,
            email: editForm.email,
            contacts: normalizedContacts,
            clientType,
            requiresMonsurvAccess
        };

        if (!isAdmin) {
            try {
                await submitFunderRequest({ requestType: 'update', funderId: editingId, payload });
                showRequestToast('Update submitted for admin approval.', 'success');
                await reloadRequests();
            } catch (e) {
                showRequestToast(e?.message || 'Failed to submit for approval.', 'error');
            }
            cancelEdit();
            return;
        }

        if (isHydratedFromApi.current) {
            try { await updateFunderOnApi(editingId, payload); } catch (e) { }
        }

        setClients(list => list.map(x => x.id === editingId ? { ...x, ...payload } : x));
        cancelEdit();
    }

    function toggleExpanded(id) {
        setExpandedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    }

    function openCardMenu(e, clientId) {
        e.preventDefault();
        e.stopPropagation();
        setMenuAnchorEl(e.currentTarget);
        setMenuClientId(String(clientId));
    }

    function closeCardMenu() {
        setMenuAnchorEl(null);
        setMenuClientId(null);
    }

    function emailClient(c) {
        if (c?.email) {
            const subject = encodeURIComponent('Inquiry from Monsurv');
            const mailto = `mailto:${c.email}?subject=${subject}`;
            window.location.href = mailto;
        }
    }

    const clientMetrics = useMemo(() => {
        const totalActiveProjects = (metrics || []).reduce((sum, row) => sum + Number(row?.active_projects || 0), 0);
        const totalActiveFacility = (metrics || []).reduce((sum, row) => sum + Number(row?.active_facility || 0), 0);
        const totalHighRisk = (metrics || []).reduce((sum, row) => sum + Number(row?.high_risk_projects || 0), 0);
        const pendingRequests = (requestItems || []).filter((item) => String(item?.status || 'pending').toLowerCase() === 'pending').length;
        return {
            clientCount: Array.isArray(clients) ? clients.length : 0,
            totalActiveProjects,
            totalActiveFacility,
            totalHighRisk,
            pendingRequests,
        };
    }, [clients, metrics, requestItems]);

    return (
        <Box className="clients" sx={{ position: 'relative', display: 'grid', gap: 2.25 }}>
            <AnalyticsPageHero
                eyebrow="Client Portfolio"
                title="Clients, requests, and portfolio relationships"
                actions={
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowClientForm(true)}>
                        {isAdmin ? 'Add client' : 'Request client change'}
                    </Button>
                }
            />

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                    gap: 2,
                }}
            >
                <AnalyticsMetricCard
                    icon={<ApartmentRoundedIcon fontSize="small" />}
                    label="Clients"
                    value={clientMetrics.clientCount}
                    caption="Active client records currently available in the client directory."
                    accent="linear-gradient(135deg, #2563eb, #0f172a)"
                />
                <AnalyticsMetricCard
                    icon={<PendingActionsRoundedIcon fontSize="small" />}
                    label="Pending Requests"
                    value={clientMetrics.pendingRequests}
                    caption="Open client change requests awaiting review in the existing approval flow."
                    accent="linear-gradient(135deg, #b45309, #ef4444)"
                />
                <AnalyticsMetricCard
                    icon={<AssessmentRoundedIcon fontSize="small" />}
                    label="Active Projects"
                    value={clientMetrics.totalActiveProjects}
                    caption="Total live projects associated with the current funder/client portfolio."
                    accent="linear-gradient(135deg, #0f766e, #14b8a6)"
                />
                <AnalyticsMetricCard
                    icon={<AttachMoneyRoundedIcon fontSize="small" />}
                    label="Active Facility"
                    value={formatCurrencyGBP(clientMetrics.totalActiveFacility)}
                    caption="Aggregate active facility value from the existing client metrics feed."
                    accent="linear-gradient(135deg, #7c3aed, #2563eb)"
                />
            </Box>

            {showClientForm && (
                <Dialog open onClose={() => setShowClientForm(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Add Client</DialogTitle>
                    <DialogContent dividers>
                        <Stack id="client-add-form" component="form" onSubmit={addClient} spacing={2} sx={{ mt: 1 }}>
                            {!isAdmin ? (
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Changes from surveyor accounts are submitted for admin approval before they appear.
                                </Typography>
                            ) : null}
                            <Box>
                                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>Funder Logo</Typography>
                                <Box
                                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const file = e.dataTransfer?.files?.[0];
                                        setLogoFileFromInput(file);
                                    }}
                                    sx={{
                                        border: '2px dashed',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        p: 2,
                                        textAlign: 'center',
                                        bgcolor: 'background.default',
                                        color: 'text.secondary'
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 600 }}>Drag & drop funder logo here</Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>JPG/PNG/WebP up to 5MB</Typography>
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={(e) => setLogoFileFromInput(e.target.files?.[0])}
                                        style={{ marginTop: 10 }}
                                        disabled={!isAdmin}
                                    />
                                    {logoFile ? (
                                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.primary' }}>
                                            Selected: <strong>{logoFile.name}</strong>
                                        </Typography>
                                    ) : null}
                                    {logoError ? (
                                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'error.main' }}>
                                            {logoError}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </Box>
                            <TextField label="Company Name" name="companyName" value={form.companyName} onChange={handleChange} fullWidth />
                            <TextField label="Head Office Address" name="headOfficeAddress" value={form.headOfficeAddress} onChange={handleChange} fullWidth />
                            {isMontechTenant ? (
                                <Stack spacing={1.2}>
                                    <FormControl fullWidth>
                                        <InputLabel id="client-type-label">Client Type</InputLabel>
                                        <Select
                                            labelId="client-type-label"
                                            label="Client Type"
                                            value={normalizeClientType(form.clientType)}
                                            onChange={(e) => setForm((f) => {
                                                const type = normalizeClientType(e.target.value);
                                                return {
                                                    ...f,
                                                    clientType: type,
                                                    requiresMonsurvAccess: normalizeRequiresMonsurvAccess(f.requiresMonsurvAccess, type)
                                                };
                                            })}
                                        >
                                            <MenuItem value={CLIENT_TYPE_BROKER}>Broker</MenuItem>
                                            <MenuItem value={CLIENT_TYPE_FUNDER_INVESTOR}>Funder / Investor</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Stack>
                            ) : null}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} fullWidth />
                                <TextField label="Company Info Email" name="email" value={form.email} onChange={handleChange} fullWidth />
                            </Stack>
                            {isMontechTenant ? (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={normalizeRequiresMonsurvAccess(form.requiresMonsurvAccess, form.clientType)}
                                            onChange={(e) => setForm((f) => ({ ...f, requiresMonsurvAccess: Boolean(e.target.checked) }))}
                                            disabled={normalizeClientType(form.clientType) === CLIENT_TYPE_BROKER}
                                        />
                                    }
                                    label="Requires Monsurv Access"
                                />
                            ) : null}
                            <Divider />
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Contacts
                                    {normalizeRequiresMonsurvAccess(form.requiresMonsurvAccess, form.clientType) ? ' (minimum 1 required)' : ''}
                                </Typography>
                                <Stack spacing={1.5}>
                                    {(form.contacts || []).map((contact, idx) => (
                                        <Stack key={`contact_${idx}`} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
                                            <TextField
                                                label="Contact Name"
                                                value={contact.name}
                                                onChange={(e) => setForm((f) => ({
                                                    ...f,
                                                    contacts: updateContactRow(f.contacts || [], idx, 'name', e.target.value)
                                                }))}
                                                fullWidth
                                            />
                                            <TextField
                                                label="Contact Number"
                                                value={contact.phone}
                                                onChange={(e) => setForm((f) => ({
                                                    ...f,
                                                    contacts: updateContactRow(f.contacts || [], idx, 'phone', e.target.value)
                                                }))}
                                                fullWidth
                                            />
                                            <TextField
                                                label="Contact Email"
                                                type="email"
                                                value={contact.email}
                                                onChange={(e) => setForm((f) => ({
                                                    ...f,
                                                    contacts: updateContactRow(f.contacts || [], idx, 'email', e.target.value)
                                                }))}
                                                fullWidth
                                                required={normalizeRequiresMonsurvAccess(form.requiresMonsurvAccess, form.clientType)}
                                            />
                                            <Button
                                                variant="text"
                                                color="error"
                                                onClick={() => setForm((f) => ({
                                                    ...f,
                                                    contacts: (f.contacts || []).filter((_, i) => i !== idx)
                                                }))}
                                                disabled={(form.contacts || []).length <= 1}
                                            >
                                                Remove
                                            </Button>
                                        </Stack>
                                    ))}
                                    <Button
                                        variant="outlined"
                                        onClick={() => setForm((f) => ({
                                            ...f,
                                            contacts: [...(f.contacts || []), { name: '', phone: '', email: '' }]
                                        }))}
                                    >
                                        Add Contact
                                    </Button>
                                </Stack>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowClientForm(false)} variant="text">Cancel</Button>
                        <Button type="submit" form="client-add-form" variant="contained">
                            {isAdmin ? 'Add Client' : 'Submit for Approval'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            <AnalyticsPanelSection
                eyebrow="Client Directory"
                title="Active clients"
                right={
                    <Paper sx={{ ...glassSubCardSx, px: 1.4, py: 1 }}>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            High risk projects
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 800 }}>
                            {clientMetrics.totalHighRisk}
                        </Typography>
                    </Paper>
                }
            >
                <Box className="clients-list" sx={{ flex: 1 }}>
                    {clients.length === 0 ? (
                        <AnalyticsInlineState
                            kind="empty"
                            title="No clients yet"
                            message={isAdmin ? 'Add your first client to start tracking portfolio exposure and requests.' : 'No clients are currently available.'}
                        />
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                alignItems: 'start',
                                gap: 2,
                            }}
                        >
                            {clients.map(c => {
                                const logoSrc = c.logo_url
                                    ? (/^https?:\/\//i.test(c.logo_url) ? c.logo_url : `${API_BASE_URL}${c.logo_url}`)
                                    : '';
                                return (
                                    <Paper
                                        key={c.id}
                                        sx={{
                                            p: 2,
                                            borderRadius: 4,
                                            position: 'relative',
                                            border: `1px solid ${alpha('#ffffff', 0.72)}`,
                                            background: (() => {
                                                const type = normalizeClientType(c.clientType ?? c.client_type);
                                                if (type === CLIENT_TYPE_BROKER) {
                                                    return `linear-gradient(180deg, ${alpha('#f3f4f6', 0.96)} 0%, ${alpha('#e5e7eb', 0.88)} 100%)`;
                                                }
                                                return `linear-gradient(180deg, ${alpha('#eff6ff', 0.96)} 0%, ${alpha('#dbeafe', 0.86)} 100%)`;
                                            })(),
                                            boxShadow: '0 18px 42px rgba(15, 23, 42, 0.08)'
                                        }}
                                    >
                                        {editingId === c.id ? (
                                            <Stack component="form" onSubmit={saveEdit} spacing={2}>
                                                {!isAdmin ? (
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        Changes will be submitted for admin approval.
                                                    </Typography>
                                                ) : null}
                                                <TextField label="Company Name" name="companyName" value={editForm.companyName} onChange={(e) => setEditForm(f => ({ ...f, companyName: e.target.value }))} fullWidth />
                                                <TextField label="Head Office Address" name="headOfficeAddress" value={editForm.headOfficeAddress} onChange={(e) => setEditForm(f => ({ ...f, headOfficeAddress: e.target.value }))} fullWidth />
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                                    <TextField label="Phone" name="phone" value={editForm.phone} onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))} fullWidth />
                                                    <TextField label="Company Info Email" name="email" value={editForm.email} onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))} fullWidth />
                                                </Stack>
                                                {isMontechTenant ? (
                                                    <Stack spacing={1.2}>
                                                        <FormControl fullWidth>
                                                            <InputLabel id={`edit-client-type-label-${String(c.id)}`}>Client Type</InputLabel>
                                                            <Select
                                                                labelId={`edit-client-type-label-${String(c.id)}`}
                                                                label="Client Type"
                                                                value={normalizeClientType(editForm.clientType)}
                                                                onChange={(e) => setEditForm((f) => {
                                                                    const type = normalizeClientType(e.target.value);
                                                                    return {
                                                                        ...f,
                                                                        clientType: type,
                                                                        requiresMonsurvAccess: normalizeRequiresMonsurvAccess(f.requiresMonsurvAccess, type)
                                                                    };
                                                                })}
                                                            >
                                                                <MenuItem value={CLIENT_TYPE_BROKER}>Broker</MenuItem>
                                                                <MenuItem value={CLIENT_TYPE_FUNDER_INVESTOR}>Funder / Investor</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={normalizeRequiresMonsurvAccess(editForm.requiresMonsurvAccess, editForm.clientType)}
                                                                    onChange={(e) => setEditForm((f) => ({ ...f, requiresMonsurvAccess: Boolean(e.target.checked) }))}
                                                                    disabled={normalizeClientType(editForm.clientType) === CLIENT_TYPE_BROKER}
                                                                />
                                                            }
                                                            label="Requires Monsurv Access"
                                                        />
                                                    </Stack>
                                                ) : null}
                                                <Divider />
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                        Contacts
                                                        {normalizeRequiresMonsurvAccess(editForm.requiresMonsurvAccess, editForm.clientType) ? ' (minimum 1 required)' : ''}
                                                    </Typography>
                                                    <Stack spacing={1.5}>
                                                        {(editForm.contacts || []).map((contact, idx) => (
                                                            <Stack key={`edit_contact_${idx}`} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
                                                                <TextField
                                                                    label="Contact Name"
                                                                    value={contact.name}
                                                                    onChange={(e) => setEditForm((f) => ({
                                                                        ...f,
                                                                        contacts: updateContactRow(f.contacts || [], idx, 'name', e.target.value)
                                                                    }))}
                                                                    fullWidth
                                                                />
                                                                <TextField
                                                                    label="Contact Number"
                                                                    value={contact.phone}
                                                                    onChange={(e) => setEditForm((f) => ({
                                                                        ...f,
                                                                        contacts: updateContactRow(f.contacts || [], idx, 'phone', e.target.value)
                                                                    }))}
                                                                    fullWidth
                                                                />
                                                                <TextField
                                                                    label="Contact Email"
                                                                    value={contact.email}
                                                                    onChange={(e) => setEditForm((f) => ({
                                                                        ...f,
                                                                        contacts: updateContactRow(f.contacts || [], idx, 'email', e.target.value)
                                                                    }))}
                                                                    fullWidth
                                                                />
                                                                <Button
                                                                    variant="text"
                                                                    color="error"
                                                                    onClick={() => setEditForm((f) => ({
                                                                        ...f,
                                                                        contacts: (f.contacts || []).filter((_, i) => i !== idx)
                                                                    }))}
                                                                    disabled={(editForm.contacts || []).length <= 1}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </Stack>
                                                        ))}
                                                        <Button
                                                            variant="outlined"
                                                            onClick={() => setEditForm((f) => ({
                                                                ...f,
                                                                contacts: [...(f.contacts || []), { name: '', phone: '', email: '' }]
                                                            }))}
                                                        >
                                                            Add Contact
                                                        </Button>
                                                    </Stack>
                                                </Box>
                                                <Stack direction="row" spacing={1}>
                                                    <Button type="submit" variant="contained">
                                                        {isAdmin ? 'Save' : 'Submit for Approval'}
                                                    </Button>
                                                    <Button type="button" variant="text" onClick={cancelEdit}>Cancel</Button>
                                                </Stack>
                                            </Stack>
                                        ) : (
                                            <>
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => openCardMenu(e, c.id)}
                                                        aria-label="Open actions"
                                                        aria-controls={menuClientId === String(c.id) && menuAnchorEl ? 'client-card-actions' : undefined}
                                                        aria-haspopup="true"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 10,
                                                            right: 14,
                                                            background: '#0b1f3a !important',
                                                            backgroundColor: '#0b1f3a !important',
                                                            color: '#fff !important',
                                                            '&:hover': {
                                                                background: '#08192e !important',
                                                                backgroundColor: '#08192e !important'
                                                            }
                                                        }}
                                                    >
                                                        <MoreVertIcon fontSize="small" />
                                                    </IconButton>
                                                    <Menu
                                                        id="client-card-actions"
                                                        anchorEl={menuAnchorEl}
                                                        open={Boolean(menuAnchorEl) && menuClientId === String(c.id)}
                                                        onClose={closeCardMenu}
                                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                    >
                                                        <MenuItem
                                                            onClick={() => {
                                                                closeCardMenu();
                                                                startEdit(c);
                                                            }}
                                                        >
                                                            <ListItemIcon>
                                                                <EditOutlinedIcon fontSize="small" />
                                                            </ListItemIcon>
                                                            Edit
                                                        </MenuItem>
                                                        <MenuItem
                                                            onClick={() => {
                                                                closeCardMenu();
                                                                emailClient(c);
                                                            }}
                                                            disabled={!c.email}
                                                        >
                                                            <ListItemIcon>
                                                                <EmailOutlinedIcon fontSize="small" />
                                                            </ListItemIcon>
                                                            Email
                                                        </MenuItem>
                                                        {isAdmin ? (
                                                            <MenuItem
                                                                onClick={async () => {
                                                                    closeCardMenu();
                                                                    await removeClient(c.id);
                                                                }}
                                                                sx={{ color: 'error.main' }}
                                                            >
                                                                <ListItemIcon sx={{ color: 'error.main' }}>
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </ListItemIcon>
                                                                Remove
                                                            </MenuItem>
                                                        ) : null}
                                                    </Menu>
                                                </>
                                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                                    <Avatar
                                                        src={logoSrc || undefined}
                                                        sx={{
                                                            width: 56,
                                                            height: 56,
                                                            bgcolor: logoSrc ? (logoStyleByClientId?.[String(c.id)]?.bgColor || '#ffffff') : 'primary.main',
                                                            border: logoSrc ? '1px solid rgba(0,0,0,0.08)' : 'none',
                                                            '& img': {
                                                                objectFit: 'contain',
                                                                padding: `${logoStyleByClientId?.[String(c.id)]?.pad ?? 6}px`
                                                            }
                                                        }}
                                                    >
                                                        {getInitials(c.companyName || c.name)}
                                                    </Avatar>
                                                    <Box sx={{ flex: 1, minWidth: 0, pr: 4 }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{c.companyName || c.name}</Typography>
                                                        <Typography variant="body2" color="text.secondary">{c.headOfficeAddress || ''}</Typography>
                                                        {(() => {
                                                            const primary = Array.isArray(c.contacts) && c.contacts.length ? c.contacts[0] : null;
                                                            const name = primary?.name || '';
                                                            const phone = primary?.phone || c.phone || '';
                                                            const email = primary?.email || c.email || '';
                                                            return (
                                                                <>
                                                                    {name ? <Typography variant="body2" color="text.secondary">{name}</Typography> : null}
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: name ? 0.5 : 0 }}>
                                                                        {phone} {email ? (`• ${email}`) : ''}
                                                                    </Typography>
                                                                </>
                                                            );
                                                        })()}
                                                    </Box>
                                                </Stack>
                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => toggleExpanded(c.id)}
                                                        aria-label={expandedIds.includes(c.id) ? 'Hide details' : 'View details'}
                                                    >
                                                        {expandedIds.includes(c.id) ? 'Hide details' : 'View details'}
                                                    </Button>
                                                    {c.email ? (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<EmailOutlinedIcon fontSize="small" />}
                                                            onClick={() => emailClient(c)}
                                                        >
                                                            Email
                                                        </Button>
                                                    ) : null}
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<EditOutlinedIcon fontSize="small" />}
                                                        onClick={() => startEdit(c)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    {isAdmin ? (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            startIcon={<DeleteOutlineIcon fontSize="small" />}
                                                            onClick={() => removeClient(c.id)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    ) : null}
                                                </Stack>
                                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                                    <IconButton
                                                        onClick={() => toggleExpanded(c.id)}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'transparent !important',
                                                            color: 'var(--muted)',
                                                            boxShadow: 'none',
                                                            '&:hover': { bgcolor: 'transparent', color: 'var(--muted)' }
                                                        }}
                                                        aria-label="Toggle metrics"
                                                        disableRipple
                                                        disableFocusRipple
                                                    >
                                                        <ExpandMoreIcon
                                                            sx={{
                                                                transition: 'transform 160ms ease',
                                                                transform: expandedIds.includes(c.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                color: 'var(--muted)',
                                                                fontSize: 26,
                                                                opacity: 1
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Box>
                                                <Collapse in={expandedIds.includes(c.id)} timeout="auto" unmountOnExit>
                                                    <Box sx={{ mt: 1, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                                        {Array.isArray(c.contacts) && c.contacts.length ? (
                                                            <Box sx={{ mb: 2 }}>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                                    Contacts
                                                                </Typography>
                                                                <Stack spacing={0.5}>
                                                                    {c.contacts.map((contact, idx) => (
                                                                        <Typography key={`${c.id}_contact_${idx}`} variant="body2">
                                                                            {contact.name || '—'}
                                                                            {contact.phone ? ` • ${contact.phone}` : ''}
                                                                            {contact.email ? ` • ${contact.email}` : ''}
                                                                        </Typography>
                                                                    ))}
                                                                </Stack>
                                                            </Box>
                                                        ) : null}
                                                        {(() => {
                                                            const key = String(c.companyName || c.name || '').trim().toLowerCase();
                                                            const m = metrics.find((x) => String(x.funder_id || '') === String(c.id || ''))
                                                                || metrics.find((x) => x.funder_key === key);
                                                            const activeProjects = m?.active_projects ?? 0;
                                                            const activeFacility = m?.active_facility ?? 0;
                                                            const initialReportsInProgress = m?.initial_reports_in_progress ?? 0;
                                                            const interimReports = m?.interim_reports ?? 0;
                                                            const highRiskProjects = m?.high_risk_projects ?? 0;
                                                            const mediumRiskProjects = m?.medium_risk_projects ?? 0;
                                                            const lowRiskProjects = m?.low_risk_projects ?? 0;
                                                            return (
                                                                <Stack spacing={1} sx={{ fontSize: 13 }}>
                                                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                                                        <Typography variant="caption" color="text.secondary">Active Projects</Typography>
                                                                        <Typography variant="caption">{activeProjects}</Typography>
                                                                    </Stack>
                                                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                                                        <Typography variant="caption" color="text.secondary">Initial Reports In Progress</Typography>
                                                                        <Typography variant="caption">{initialReportsInProgress}</Typography>
                                                                    </Stack>
                                                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                                                        <Typography variant="caption" color="text.secondary">Interim Reports</Typography>
                                                                        <Typography variant="caption">{interimReports}</Typography>
                                                                    </Stack>
                                                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                                                        <Typography variant="caption" color="text.secondary">High Risk Projects</Typography>
                                                                        <Typography variant="caption">{highRiskProjects}</Typography>
                                                                    </Stack>
                                                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                                                        <Typography variant="caption" color="text.secondary">Medium Risk Projects</Typography>
                                                                        <Typography variant="caption">{mediumRiskProjects}</Typography>
                                                                    </Stack>
                                                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                                                        <Typography variant="caption" color="text.secondary">Low Risk Projects</Typography>
                                                                        <Typography variant="caption">{lowRiskProjects}</Typography>
                                                                    </Stack>
                                                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                                                        <Typography variant="caption" color="text.secondary">Active Facility</Typography>
                                                                        <Typography variant="caption">{formatCurrencyGBP(activeFacility)}</Typography>
                                                                    </Stack>
                                                                </Stack>
                                                            );
                                                        })()}
                                                    </Box>
                                                </Collapse>
                                            </>
                                        )}
                                    </Paper>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </AnalyticsPanelSection>

            <Snackbar
                open={Boolean(requestNotice)}
                onClose={() => setRequestNotice('')}
                autoHideDuration={(requestNoticeSeverity === 'success') ? 5000 : 7000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setRequestNotice('')} severity={requestNoticeSeverity} variant="filled" sx={{ width: '100%' }}>
                    {requestNotice}
                </Alert>
            </Snackbar>

            {(isAdmin || isSurveyor) ? (
                <AnalyticsPanelSection
                    eyebrow="Approval Queue"
                    title={isAdmin ? 'Pending client approvals' : 'Your client change requests'}
                >
                    {requestsLoading ? (
                        <AnalyticsInlineState kind="loading" message="Loading requests..." compact />
                    ) : null}
                    {requestsError ? (
                        <AnalyticsInlineState
                            kind="error"
                            title="Unable to load requests"
                            message={requestsError}
                            actionLabel="Retry"
                            onAction={reloadRequests}
                        />
                    ) : null}
                    {!requestsLoading && !requestsError && requestItems.length === 0 ? (
                        <AnalyticsInlineState
                            kind="empty"
                            title={isAdmin ? 'No pending approvals' : 'No requests submitted yet'}
                            message={isAdmin ? 'Incoming client change approvals will appear here.' : 'Client change requests you submit will appear here with status updates.'}
                        />
                    ) : null}
                    <Stack spacing={1.25}>
                        {requestItems.map((item) => {
                            const payload = item?.payload && typeof item.payload === 'object' ? item.payload : {};
                            const title = payload.companyName || item.funder_name || 'Client change';
                            const requestedBy = item.requested_by_display || item.requested_by || 'Unknown';
                            const status = String(item.status || 'pending');
                            const pending = status.toLowerCase() === 'pending';
                            return (
                                <Paper
                                    key={`req_${item.id}`}
                                    onClick={isAdmin ? () => openAdminReview(item) : undefined}
                                    sx={{
                                        p: 1.6,
                                        borderRadius: 3.5,
                                        cursor: isAdmin ? 'pointer' : 'default',
                                        border: `1px solid ${alpha(pending ? '#fde68a' : '#bfdbfe', 0.7)}`,
                                        background: pending
                                            ? `linear-gradient(180deg, ${alpha('#fffaf0', 0.96)} 0%, ${alpha('#fff7ed', 0.88)} 100%)`
                                            : `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#f8fbff', 0.78)} 100%)`,
                                        boxShadow: '0 14px 32px rgba(15, 23, 42, 0.07)',
                                        '&:hover': isAdmin ? { transform: 'translateY(-1px)', boxShadow: '0 18px 36px rgba(15, 23, 42, 0.09)' } : undefined,
                                        transition: 'transform 160ms ease, box-shadow 160ms ease'
                                    }}
                                >
                                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.25}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{title}</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4 }}>
                                                {item.request_type} • {status} • {requestedBy}
                                                {isAdmin ? ' • Click to review' : ''}
                                            </Typography>
                                        </Box>
                                        <Paper sx={{ ...glassSubCardSx, px: 1.15, py: 0.8, alignSelf: 'flex-start' }}>
                                            <Typography variant="caption" sx={{ color: pending ? 'warning.dark' : 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                {status}
                                            </Typography>
                                        </Paper>
                                    </Stack>
                                </Paper>
                            );
                        })}
                    </Stack>
                </AnalyticsPanelSection>
            ) : null}

            {isAdmin ? (() => {
                const reqId = String(adminReviewItem?.id ?? '');
                const payload = adminReviewItem?.payload && typeof adminReviewItem.payload === 'object' ? adminReviewItem.payload : {};
                const title = (payload?.companyName || adminReviewItem?.funder_name || 'Client change');
                const requestedBy = adminReviewItem?.requested_by_display || adminReviewItem?.requested_by || 'Unknown';
                const status = String(adminReviewItem?.status || 'pending');
                const editPayload = reqId ? (adminRequestEdits?.[reqId] || normalizeRequestPayload(payload)) : null;

                return (
                    <Dialog open={adminReviewOpen} onClose={closeAdminReview} maxWidth="md" fullWidth>
                        <DialogTitle sx={{ pr: 6 }}>
                            Review Client Request
                            <IconButton
                                aria-label="Close"
                                onClick={closeAdminReview}
                                sx={{ position: 'absolute', right: 8, top: 8 }}
                            >
                                <CloseIcon />
                            </IconButton>
                            <Typography variant="subtitle2" sx={{ mt: 0.5, fontWeight: 700 }}>{title}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {adminReviewItem?.request_type} • {status} • {requestedBy}
                            </Typography>
                        </DialogTitle>
                        <DialogContent dividers>
                            {adminReviewItem ? (
                                <Stack spacing={2}>
                                    <TextField
                                        label="Company Name"
                                        value={editPayload?.companyName || ''}
                                        onChange={(e) => setAdminRequestEdits((prev) => ({
                                            ...(prev || {}),
                                            [reqId]: { ...(prev?.[reqId] || editPayload), companyName: e.target.value }
                                        }))}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Head Office Address"
                                        value={editPayload?.headOfficeAddress || ''}
                                        onChange={(e) => setAdminRequestEdits((prev) => ({
                                            ...(prev || {}),
                                            [reqId]: { ...(prev?.[reqId] || editPayload), headOfficeAddress: e.target.value }
                                        }))}
                                        fullWidth
                                    />
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField
                                            label="Phone"
                                            value={editPayload?.phone || ''}
                                            onChange={(e) => setAdminRequestEdits((prev) => ({
                                                ...(prev || {}),
                                                [reqId]: { ...(prev?.[reqId] || editPayload), phone: e.target.value }
                                            }))}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Company Info Email"
                                            value={editPayload?.email || ''}
                                            onChange={(e) => setAdminRequestEdits((prev) => ({
                                                ...(prev || {}),
                                                [reqId]: { ...(prev?.[reqId] || editPayload), email: e.target.value }
                                            }))}
                                            fullWidth
                                        />
                                    </Stack>

                                    {isMontechTenant ? (
                                        <Stack spacing={1.2}>
                                            <FormControl fullWidth>
                                                <InputLabel id="admin-review-client-type-label">Client Type</InputLabel>
                                                <Select
                                                    labelId="admin-review-client-type-label"
                                                    label="Client Type"
                                                    value={normalizeClientType(editPayload?.clientType ?? editPayload?.client_type)}
                                                    onChange={(e) => {
                                                        const type = normalizeClientType(e.target.value);
                                                        setAdminRequestEdits((prev) => ({
                                                            ...(prev || {}),
                                                            [reqId]: {
                                                                ...(prev?.[reqId] || editPayload),
                                                                clientType: type,
                                                                requiresMonsurvAccess: normalizeRequiresMonsurvAccess(
                                                                    (prev?.[reqId] || editPayload)?.requiresMonsurvAccess,
                                                                    type
                                                                )
                                                            }
                                                        }));
                                                    }}
                                                >
                                                    <MenuItem value={CLIENT_TYPE_BROKER}>Broker</MenuItem>
                                                    <MenuItem value={CLIENT_TYPE_FUNDER_INVESTOR}>Funder / Investor</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={normalizeRequiresMonsurvAccess(
                                                            editPayload?.requiresMonsurvAccess ?? editPayload?.requires_monsurv_access,
                                                            editPayload?.clientType ?? editPayload?.client_type
                                                        )}
                                                        onChange={(e) => setAdminRequestEdits((prev) => ({
                                                            ...(prev || {}),
                                                            [reqId]: {
                                                                ...(prev?.[reqId] || editPayload),
                                                                requiresMonsurvAccess: Boolean(e.target.checked)
                                                            }
                                                        }))}
                                                        disabled={normalizeClientType(editPayload?.clientType ?? editPayload?.client_type) === CLIENT_TYPE_BROKER}
                                                    />
                                                }
                                                label="Requires Monsurv Access"
                                            />
                                        </Stack>
                                    ) : null}

                                    <Divider />

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                            Contacts
                                            {normalizeRequiresMonsurvAccess(
                                                editPayload?.requiresMonsurvAccess ?? editPayload?.requires_monsurv_access,
                                                editPayload?.clientType ?? editPayload?.client_type
                                            ) ? ' (minimum 1 required)' : ''}
                                        </Typography>
                                        <Stack spacing={1.5}>
                                            {(Array.isArray(editPayload?.contacts) && editPayload.contacts.length ? editPayload.contacts : [{ name: '', phone: '', email: '' }]).map((contact, idx) => (
                                                <Stack key={`req_${reqId}_contact_${idx}`} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
                                                    <TextField
                                                        label="Contact Name"
                                                        value={contact.name || ''}
                                                        onChange={(e) => setAdminRequestEdits((prev) => ({
                                                            ...(prev || {}),
                                                            [reqId]: {
                                                                ...(prev?.[reqId] || editPayload),
                                                                contacts: updateContactRow((prev?.[reqId]?.contacts || editPayload?.contacts || []), idx, 'name', e.target.value)
                                                            }
                                                        }))}
                                                        fullWidth
                                                    />
                                                    <TextField
                                                        label="Contact Number"
                                                        value={contact.phone || ''}
                                                        onChange={(e) => setAdminRequestEdits((prev) => ({
                                                            ...(prev || {}),
                                                            [reqId]: {
                                                                ...(prev?.[reqId] || editPayload),
                                                                contacts: updateContactRow((prev?.[reqId]?.contacts || editPayload?.contacts || []), idx, 'phone', e.target.value)
                                                            }
                                                        }))}
                                                        fullWidth
                                                    />
                                                    <TextField
                                                        label="Contact Email"
                                                        value={contact.email || ''}
                                                        onChange={(e) => setAdminRequestEdits((prev) => ({
                                                            ...(prev || {}),
                                                            [reqId]: {
                                                                ...(prev?.[reqId] || editPayload),
                                                                contacts: updateContactRow((prev?.[reqId]?.contacts || editPayload?.contacts || []), idx, 'email', e.target.value)
                                                            }
                                                        }))}
                                                        fullWidth
                                                    />
                                                    <Button
                                                        variant="text"
                                                        color="error"
                                                        onClick={() => setAdminRequestEdits((prev) => ({
                                                            ...(prev || {}),
                                                            [reqId]: {
                                                                ...(prev?.[reqId] || editPayload),
                                                                contacts: (prev?.[reqId]?.contacts || editPayload?.contacts || []).filter((_, i) => i !== idx)
                                                            }
                                                        }))}
                                                        disabled={(editPayload?.contacts || []).length <= 1}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Stack>
                                            ))}
                                            <Button
                                                variant="outlined"
                                                onClick={() => setAdminRequestEdits((prev) => ({
                                                    ...(prev || {}),
                                                    [reqId]: {
                                                        ...(prev?.[reqId] || editPayload),
                                                        contacts: [...(prev?.[reqId]?.contacts || editPayload?.contacts || []), { name: '', phone: '', email: '' }]
                                                    }
                                                }))}
                                            >
                                                Add Contact
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">Select a request to review.</Typography>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'space-between' }}>
                            <Button
                                variant="text"
                                onClick={() => {
                                    if (!reqId) return;
                                    setAdminRequestEdits((prev) => ({
                                        ...(prev || {}),
                                        [reqId]: normalizeRequestPayload(payload)
                                    }));
                                }}
                                disabled={!adminReviewItem}
                            >
                                Reset
                            </Button>
                            <Stack direction="row" spacing={1}>
                                <Button onClick={closeAdminReview} variant="outlined">Close</Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    disabled={!adminReviewItem}
                                    onClick={async () => {
                                        if (!adminReviewItem) return;
                                        openRejectDialog(adminReviewItem.id);
                                    }}
                                >
                                    Reject
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={!adminReviewItem}
                                    onClick={async () => {
                                        if (!adminReviewItem) return;
                                        try {
                                            await approveRequest(adminReviewItem.id, editPayload);
                                            showRequestToast('Approved.', 'success');
                                            closeAdminReview();
                                            await reloadClients();
                                            await reloadRequests();
                                        } catch (e) {
                                            showRequestToast(e?.message || 'Failed to approve request.', 'error');
                                        }
                                    }}
                                >
                                    Approve
                                </Button>
                            </Stack>
                        </DialogActions>
                    </Dialog>
                );
            })() : null}

            <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Delete {deleteTargetLabel || 'client'}</DialogTitle>
                <DialogContent>
                    {deleteError ? <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert> : null}
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This will permanently delete this client. This cannot be undone.
                    </Alert>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Deletion password"
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        disabled={deleteBusy}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={deleteBusy}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmDeleteClient} disabled={deleteBusy}>
                        {deleteBusy ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={rejectDialogOpen} onClose={closeRejectDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Reject request</DialogTitle>
                <DialogContent>
                    {rejectError ? <Alert severity="error" sx={{ mb: 2 }}>{rejectError}</Alert> : null}
                    <TextField
                        autoFocus
                        fullWidth
                        label="Rejection note (optional)"
                        value={rejectNote}
                        onChange={(e) => setRejectNote(e.target.value)}
                        disabled={rejectBusy}
                        multiline
                        minRows={3}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRejectDialog} disabled={rejectBusy}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmRejectRequest} disabled={rejectBusy}>
                        {rejectBusy ? 'Rejecting…' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
